/**
 * useEdicionesP2 — almacén, por id de fila, de lo que corrige el técnico en P2 (`C0-e3`).
 *
 * Hasta `C0-e3` lo corregido en P2 solo vivía en el estado de `useNormalization`
 * y `useAutoGeocoding` (doble parche de `C0-c`). Tras recargar y pulsar
 * «Recuperar», al volver a P2 la normalización y la geolocalización se rehacen
 * desde el documento y lo corregido se perdía; y la geolocalización, al terminar,
 * publica las filas con que empezó, así que una corrección hecha mientras corría
 * también se perdía (`AUDIT_C0E3.md` H1).
 *
 * DOP selladas por Luis el 25-oct-2026 («adelante», tras la auditoría):
 * - DOP-1 (a): se guarda aquí cada corrección (campos de `CAMPOS_EDITABLES`),
 *   fundida en orden. `WizardPageV2` la aplica al leer, encima de lo que den los
 *   motores, y la guarda en la caché de sesión (campo aditivo, sin subir versión).
 *   El doble parche se queda: aplicar dos veces lo mismo no cambia nada.
 * - DOP-2 (a): lo que vuelve de `sessionStorage` se valida campo a campo; lo
 *   imposible se descarta con `console.error` (HX-FALLBACK-ENMASCARA-BUG).
 * - DOP-3: se vacía con el documento. Los ids de fila son aleatorios por
 *   extracción y estables tras «Recuperar» (el documento sale de la caché).
 */

import { useCallback, useReducer } from 'react';
import type { ProcessedInfrastructure } from '@/types/processing';

/** Lo que P2 puede corregir: nombre y dirección, X/Y, y la confirmación con su puntuación. */
export const CAMPOS_EDITABLES = [
  'nombre', 'direccion', 'xFinal', 'yFinal', 'confirmado', 'confirmedAt', 'score', 'scoreDetail',
] as const;

export type CampoEditable = (typeof CAMPOS_EDITABLES)[number];
export type EdicionFila = Partial<Pick<ProcessedInfrastructure, CampoEditable>>;
/** Mapa id de fila → lo corregido en esa fila. */
export type AlmacenEdiciones = Readonly<Record<string, EdicionFila>>;

export type AccionEdicion =
  | { readonly tipo: 'editar'; readonly id: string; readonly cambio: EdicionFila }
  | { readonly tipo: 'vaciar' }
  /** Repone lo guardado en la caché de sesión al pulsar «Recuperar». Sustituye el almacén entero. */
  | { readonly tipo: 'reponer'; readonly guardado: unknown };

export const EDICIONES_VACIAS: AlmacenEdiciones = Object.freeze({});

const esNumeroFinito = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);
const esFecha = (v: unknown): boolean => typeof v === 'string' && !Number.isNaN(new Date(v).getTime());
const esObjeto = (v: unknown): boolean => !!v && typeof v === 'object' && !Array.isArray(v);

/** Qué admite cada campo al reponer. */
const VALIDA_CAMPO: Record<CampoEditable, (v: unknown) => boolean> = {
  nombre: v => typeof v === 'string',
  direccion: v => typeof v === 'string',
  xFinal: esNumeroFinito,
  yFinal: esNumeroFinito,
  confirmado: v => typeof v === 'boolean',
  confirmedAt: esFecha,
  score: esNumeroFinito,
  scoreDetail: esObjeto,
};

function esCampoEditable(c: string): c is CampoEditable {
  return (CAMPOS_EDITABLES as readonly string[]).includes(c);
}

/**
 * DOP-2 (a): valida lo que viene de `sessionStorage` antes de reponerlo. Cada
 * campo debe ser editable y de su tipo; los que no, se descartan con
 * `console.error` y se conserva el resto de la fila.
 */
export function validarEdicionesGuardadas(guardado: unknown): AlmacenEdiciones {
  if (guardado === undefined || guardado === null) return EDICIONES_VACIAS;
  if (!esObjeto(guardado)) {
    console.error('[useEdicionesP2] Las ediciones guardadas no son un mapa; no se reponen.', guardado);
    return EDICIONES_VACIAS;
  }
  const salida: Record<string, EdicionFila> = {};
  for (const [id, edicion] of Object.entries(guardado as Record<string, unknown>)) {
    if (!esObjeto(edicion)) {
      console.error(`[useEdicionesP2] Edición guardada inválida para la fila ${id}; se descarta.`, edicion);
      continue;
    }
    const fila: Record<string, unknown> = {};
    for (const [campo, valor] of Object.entries(edicion as Record<string, unknown>)) {
      if (!esCampoEditable(campo) || !VALIDA_CAMPO[campo](valor)) {
        console.error(`[useEdicionesP2] Campo «${campo}» inválido en la fila ${id}; se descarta.`, valor);
        continue;
      }
      fila[campo] = valor;
    }
    if (Object.keys(fila).length > 0) salida[id] = fila as EdicionFila;
  }
  return Object.keys(salida).length === 0 ? EDICIONES_VACIAS : salida;
}

export function reducirEdiciones(estado: AlmacenEdiciones, accion: AccionEdicion): AlmacenEdiciones {
  if (accion.tipo === 'vaciar') {
    return Object.keys(estado).length === 0 ? estado : EDICIONES_VACIAS;
  }
  if (accion.tipo === 'reponer') {
    const repuesto = validarEdicionesGuardadas(accion.guardado);
    return repuesto === EDICIONES_VACIAS && Object.keys(estado).length === 0 ? estado : repuesto;
  }
  const { id, cambio } = accion;
  return { ...estado, [id]: { ...estado[id], ...cambio } };
}

/**
 * Aplica lo corregido encima de las filas que dan los motores. Devuelve el
 * mismo array si ninguna fila cambia (sin ediciones, o con el doble parche ya
 * hecho): así los efectos que dependen de las filas no se disparan de más
 * (`AUDIT_C0E3.md` H2). Las filas sin edición conservan su objeto.
 */
export function aplicarEdiciones<T extends ProcessedInfrastructure>(
  filas: T[],
  almacen: AlmacenEdiciones,
): T[] {
  if (filas.length === 0 || Object.keys(almacen).length === 0) return filas;
  let cambiado = false;
  const salida = filas.map(fila => {
    const edicion = almacen[fila.id];
    if (!edicion) return fila;
    const yaAplicada = (Object.keys(edicion) as CampoEditable[]).every(c => fila[c] === edicion[c]);
    if (yaAplicada) return fila;
    cambiado = true;
    return { ...fila, ...edicion };
  });
  return cambiado ? salida : filas;
}

export interface UseEdicionesP2 {
  readonly ediciones: AlmacenEdiciones;
  readonly editar: (id: string, cambio: EdicionFila) => void;
  readonly vaciar: () => void;
  /** Repone lo guardado en la caché (validado campo a campo). */
  readonly reponer: (guardado: unknown) => void;
}

export function useEdicionesP2(): UseEdicionesP2 {
  const [ediciones, despachar] = useReducer(reducirEdiciones, EDICIONES_VACIAS);
  const editar = useCallback((id: string, cambio: EdicionFila) => despachar({ tipo: 'editar', id, cambio }), []);
  const vaciar = useCallback(() => despachar({ tipo: 'vaciar' }), []);
  const reponer = useCallback((guardado: unknown) => despachar({ tipo: 'reponer', guardado }), []);
  return { ediciones, editar, vaciar, reponer };
}
